/**
 * @xivdyetools/logger - Console Adapter
 *
 * Logger adapter that outputs to the console with pretty formatting.
 * Best for development and debugging.
 *
 * @module adapters/console-adapter
 */
import { BaseLogger, safeStringify } from '../core/base-logger.js';
/**
 * Console logger adapter with pretty or JSON formatting
 *
 * Outputs logs to the console using appropriate console methods
 * (console.debug, console.info, console.warn, console.error).
 *
 * Implementation detail — consumers should use `createBrowserLogger`,
 * `createLibraryLogger`, `ConsoleLogger`, or `browserLogger` instead.
 *
 * @example
 * ```typescript
 * const logger = new ConsoleAdapter({ format: 'pretty', prefix: 'MyApp' });
 * logger.info('Server started', { port: 3000 });
 * // Output: [2024-01-15T10:30:00.000Z] [MyApp] Server started {"port":3000}
 * ```
 */
export class ConsoleAdapter extends BaseLogger {
    constructor(config = {}) {
        super({
            format: 'pretty',
            ...config,
        });
    }
    write(entry) {
        if (this.config.format === 'pretty') {
            this.writePretty(entry);
        }
        else {
            this.writeJson(entry);
        }
    }
    /**
     * Write a log entry with pretty formatting
     */
    writePretty(entry) {
        const { level, message, timestamp, context, error } = entry;
        // Build the log line
        const parts = [];
        if (this.config.timestamps) {
            parts.push(`[${timestamp}]`);
        }
        parts.push(message);
        // Add context as JSON if present.
        //
        // BUG-104 (deep dive 2026-09-02): raw `JSON.stringify` threw out of the
        // log call on a circular or BigInt context — `TypeError: Converting
        // circular structure to JSON` / `Do not know how to serialize a BigInt` —
        // taking the CALLER down with it. That is the exact failure FINDING-026
        // fixed, applied to `JsonAdapter` only; this adapter is what
        // `ConsoleLogger`, `browserLogger`, `createBrowserLogger` and
        // `createLibraryLogger` (stoat-worker) all use, and it is `@public`.
        if (context && Object.keys(context).length > 0) {
            parts.push(safeStringify(context));
        }
        const logLine = parts.join(' ');
        // Use appropriate console method
        switch (level) {
            case 'debug':
                if (error) {
                    console.debug(logLine, error);
                }
                else {
                    console.debug(logLine);
                }
                break;
            case 'info':
                if (error) {
                    console.info(logLine, error);
                }
                else {
                    console.info(logLine);
                }
                break;
            case 'warn':
                if (error) {
                    console.warn(logLine, error);
                }
                else {
                    console.warn(logLine);
                }
                break;
            case 'error':
                if (error) {
                    console.error(logLine, error);
                }
                else {
                    console.error(logLine);
                }
                break;
        }
    }
    /**
     * Write a log entry as JSON
     */
    writeJson(entry) {
        // BUG-104: same reason as `writePretty` above — `JsonAdapter` has used
        // `safeStringify` since FINDING-026 and this adapter never caught up.
        const jsonStr = safeStringify(entry);
        switch (entry.level) {
            case 'debug':
                console.debug(jsonStr);
                break;
            case 'info':
                console.info(jsonStr);
                break;
            case 'warn':
                console.warn(jsonStr);
                break;
            case 'error':
                console.error(jsonStr);
                break;
        }
    }
}
//# sourceMappingURL=console-adapter.js.map